import React, { useMemo, useState } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Select,
  Modal,
  Form,
  Input,
  Switch,
  Popconfirm,
  Typography,
  App,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDepartments, useDepartmentsForSelection, useCreateDepartment, useUpdateDepartment, useDeleteDepartment } from '@/hooks/useDepartments';
import type { Department } from '@/services/department.service';
import { userService } from '@/services/user.service';

const { Title } = Typography;

function userLabel(u: { first_name: string; last_name?: string; email: string; username: string }): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} (${u.email})` : `${u.username} (${u.email})`;
}

const DepartmentsPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { message } = App.useApp();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingRecord, setEditingRecord] = useState<Department | null>(null);
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  const [form] = Form.useForm();

  const filters = { is_active: activeFilter };
  const { data: list = [], isLoading } = useDepartments(filters);
  const { data: selectionList = [] } = useDepartmentsForSelection();
  const { data: users = [] } = useQuery({
    queryKey: ['users-for-department-process-owner'],
    queryFn: () => userService.getAll({ is_active: true }),
  });
  const createMutation = useCreateDepartment();
  const updateMutation = useUpdateDepartment();
  const deleteMutation = useDeleteDepartment();

  const userNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) map.set(u.id, userLabel(u));
    return map;
  }, [users]);

  const applyFormValues = (record: Department | null) => {
    if (!record) {
      form.resetFields();
      form.setFieldsValue({ is_active: true });
      return;
    }
    form.setFieldsValue({
      department_code: record.department_code,
      department_name: record.department_name,
      parent_department_id: record.parent_department_id || undefined,
      department_head: record.department_head || undefined,
      cost_center_code: record.cost_center_code,
      description: record.description,
      is_active: record.is_active,
    });
  };

  const handleAdd = () => {
    setEditingId(null);
    setEditingRecord(null);
    setModalOpen(true);
  };

  const handleEdit = (record: Department) => {
    setEditingId(record.id);
    setEditingRecord(record);
    setModalOpen(true);
  };

  /** destroyOnClose remounts Form — set values only after the modal (and Form) are open. */
  const handleAfterOpenChange = (open: boolean) => {
    if (!open) return;
    applyFormValues(editingRecord);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, payload: values });
      } else {
        await createMutation.mutateAsync(values);
      }
      setModalOpen(false);
      setEditingId(null);
      setEditingRecord(null);
      form.resetFields();
    } catch (e) {
      if (e && typeof (e as any).errorFields !== 'undefined') return;
      message.error((e as Error)?.message || t('saveFailed'));
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
    } catch {
      // error already shown in hook
    }
  };

  const columns: ColumnsType<Department> = [
    { title: t('code'), dataIndex: 'department_code', key: 'department_code', width: 120 },
    { title: t('name'), dataIndex: 'department_name', key: 'department_name', ellipsis: true },
    {
      title: t('processOwner'),
      dataIndex: 'department_head',
      key: 'department_head',
      width: 200,
      ellipsis: true,
      render: (v?: string) => (v ? userNameById.get(v) || v : '—'),
    },
    { title: t('costCenter'), dataIndex: 'cost_center_code', key: 'cost_center_code', width: 120 },
    {
      title: t('parent'),
      key: 'parent',
      width: 160,
      render: (_, r) => r.parentDepartment?.department_name ?? (r.parent_department_id ? '-' : '-'),
    },
    {
      title: t('active'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (v: boolean) => <Tag color={v ? 'green' : 'default'}>{v ? tc('yes') : tc('no')}</Tag>,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            {tc('edit')}
          </Button>
          <Popconfirm
            title={t('confirmDeleteDepartment')}
            onConfirm={() => handleDelete(record.id)}
            okText={tc('yes')}
            cancelText={tc('no')}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              {tc('delete')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Title level={4} style={{ margin: 0 }}>
            {t('departments')}
          </Title>
          <Select
            placeholder={t('status')}
            allowClear
            style={{ width: 120 }}
            value={activeFilter === undefined ? undefined : activeFilter}
            onChange={(v) => setActiveFilter(v)}
            options={[
              { value: true, label: t('active') },
              { value: false, label: t('inactive') },
            ]}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            {t('addDepartment')}
          </Button>
        </Space>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={isLoading}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
          scroll={{ x: 960 }}
        />
      </Card>

      <Modal
        title={editingId ? t('editDepartment') : t('newDepartment')}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={() => {
          setModalOpen(false);
          setEditingId(null);
          setEditingRecord(null);
        }}
        afterOpenChange={handleAfterOpenChange}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        destroyOnClose
        width={480}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="department_code"
            label={t('departmentCode')}
            rules={[{ required: true, message: t('fieldRequired') }]}
          >
            <Input placeholder={t('departmentCodePlaceholder')} disabled={!!editingId} />
          </Form.Item>
          <Form.Item
            name="department_name"
            label={t('departmentName')}
            rules={[{ required: true, message: t('fieldRequired') }]}
          >
            <Input placeholder={t('departmentNamePlaceholder')} />
          </Form.Item>
          <Form.Item name="parent_department_id" label={t('parentDepartment')}>
            <Select
              allowClear
              placeholder={t('selectParent')}
              options={selectionList.filter((d) => d.id !== editingId).map((d) => ({ value: d.id, label: `${d.department_code} - ${d.department_name}` }))}
            />
          </Form.Item>
          <Form.Item
            name="department_head"
            label={t('processOwner')}
            extra={t('processOwnerExtra')}
          >
            <Select
              allowClear
              showSearch
              placeholder={t('selectUser')}
              optionFilterProp="label"
              options={users.map((u) => ({ value: u.id, label: userLabel(u) }))}
            />
          </Form.Item>
          <Form.Item name="cost_center_code" label={t('costCenterCode')}>
            <Input placeholder={t('optional')} />
          </Form.Item>
          <Form.Item name="description" label={t('description')}>
            <Input.TextArea rows={2} placeholder={t('optional')} />
          </Form.Item>
          <Form.Item name="is_active" label={t('active')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default DepartmentsPage;
